<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <header
      class="
        tw-sticky
        tw-top-0
        tw-bg-sf-snow-lighter
        tw-z-10
        tw-flex
        tw-items-center
        tw-px-6
        tw-py-3
        tw-h-[var(--secondary-subheader-height)]
        tw-border-solid
        tw-border-b
        tw-border-x-0
        tw-border-t-0
        tw-border-sf-neutral-lighter-2
      "
    >
      <h3
        class="tw-text-base"
        :class="{
          'tw-font-normal': attachingMode !== attachingModeConstant.NEW_FILES,
        }"
      >
        <template v-if="attachingMode === attachingModeConstant.EXISTING_FILES">
          <SfLink class="!tw-no-underline !tw-inline" @click="resetUI">
            {{ I18n.t('file_attachments_sidebar.header.title') }}
          </SfLink>
          <span class="tw-mx-1">/</span>
          <strong>
            {{
              I18n.t('file_attachments_sidebar.header.subtitle.existing_files')
            }}
          </strong>
        </template>
        <template
          v-if="attachingMode === attachingModeConstant.EXISTING_EXPORTS"
        >
          <SfLink class="!tw-no-underline !tw-inline" @click="resetUI">
            {{ I18n.t('file_attachments_sidebar.header.title') }}
          </SfLink>
          <span class="tw-mx-1">/</span>
          <strong>
            {{
              I18n.t(
                'file_attachments_sidebar.header.subtitle.existing_exports'
              )
            }}
          </strong>
        </template>
        <template v-if="attachingMode === attachingModeConstant.EXTERNAL_LINK">
          <SfLink class="!tw-no-underline !tw-inline" @click="resetUI">
            {{ I18n.t('file_attachments_sidebar.header.title') }}
          </SfLink>
          <span class="tw-mx-1">/</span>
          <strong>
            {{ externalManagerTitle }}
          </strong>
        </template>
        <template v-if="attachingMode === attachingModeConstant.NEW_FILES">
          {{ I18n.t('file_attachments_sidebar.header.title') }}
          <template v-if="attachmentsCount">({{ attachmentsCount }})</template>
        </template>
      </h3>
      <div class="tw-ml-auto">
        <SfButtonIcon icon="xmark" @click="handleDrawerClose" />
      </div>
    </header>

    <div class="tw-p-6 tw-text-sf-neutral tw-flex-grow tw-h-full">
      <SelectExistingDocument
        v-if="attachingMode === attachingModeConstant.EXISTING_FILES"
        :show-exports="false"
        for-sidebar
        @attach="attachExistingDocuments"
        @back="resetUI"
      />
      <SelectExistingDocument
        v-if="attachingMode === attachingModeConstant.EXISTING_EXPORTS"
        show-exports-only
        for-sidebar
        @attach="attachExistingExports"
        @back="resetUI"
      />
      <ExternalLinkManager
        v-if="attachingMode === attachingModeConstant.EXTERNAL_LINK"
        :id="currentExternalLink?.document_id"
        :name="currentExternalLink?.name"
        :url="currentExternalLink?.url"
        @add-link="handleAttachExternalLink"
        @cancel="resetUI"
      />

      <template v-if="attachingMode === attachingModeConstant.NEW_FILES">
        <AddAttachments
          :before-upload="beforeUpload"
          @on-file-added="handleFileAdded"
          @on-file-uploaded="handleFileUploaded"
          @on-all-files-uploaded="handleAllFilesUploaded"
          @on-file-upload-progress="handleFileUploadProgress"
          @change-mode="changeMode"
        />
        <SfBannerPage
          v-if="errorMessage"
          variant="danger"
          :title="errorMessage"
          class="tw-mb-4"
          show-close
          @close="clearError"
        />
        <AttachmentList
          :attachments="attachments"
          :changes-are-disabled="changesAreDisabled"
          @delete-attachment="deleteAttachment"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  Attachment,
  AttachmentToDelete,
  NewAttachment,
  UpdateTextPropertyErrorResponse,
} from './types'
import type {
  ExistingDocument,
  ExistingExport,
  ExistingPermanentDocument,
  LiquidDocument,
  RawExistingPermanentDocument,
  RawExistingDocument,
  RawExistingExport,
} from '@app/shared/types/sf-data'

import { storeToRefs } from 'pinia'
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'

import AddAttachments from './add-attachments.vue'
import { checkForDuplicates as checkForDuplicateRequest } from './api'
import { generateStorageKey } from './utils'
import AttachmentList from './attachment-list.vue'
import { attachingMode as attachingModeConstant } from './constants'
import { SfLog } from '@app/adapters/logger'
import ExternalLinkManager from '@app/application/external-links/components/external-link-manager.vue'
import SelectExistingDocument from '@app/application/reconciliation/sections/select-existing-document.vue'
import SfBannerPage from '@app/shared/vue/components/sf-banner/sf-banner-page.vue'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button'
import SfLink from '@app/shared/vue/components/sf-link.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { DocumentLinkType } from '@app/shared/types/sf-data'

import { useSidebarFileAttachmentsStore } from './store/sidebar-file-attachments'
import { useFileAttachmentsLiveInputStore } from './store/file-attachments-live-input'

import { useDrawer } from '@app/shared/pinia/drawer'

const I18n = useI18n()
const drawerStore = useDrawer()
const { isRightDrawerOpen } = storeToRefs(drawerStore)
const fileAttachmentsLiveInputStore = useFileAttachmentsLiveInputStore()
const { textPropertyName, textPropertyObjectId } = storeToRefs(
  fileAttachmentsLiveInputStore
)

const store = useSidebarFileAttachmentsStore()

const {
  attachments,
  attachmentsCount,
  attachingMode,
  changesAreDisabled,
  currentExternalLink,
  errorMessage,
} = storeToRefs(store)

const {
  changeMode,
  clearAutoLinkedAttachments,
  clearError,
  clearExternalLink,
  handleFileAdded,
  handleFileUploadProgress,
  handleFileUploaded,
  closeExistingFilesBrowser,
  loadAttachments,
  markAsAutoLinked,
  cancelActiveUploads,
  toggleChangesAreDisabled,
  raiseError,
} = store

const pendingAutoLinkedAttachments = ref<NewAttachment[]>([])

const { closeRightDrawerWithoutPersisting } = drawerStore

watch([textPropertyName, textPropertyObjectId], () => {
  clearAutoLinkedAttachments()
  pendingAutoLinkedAttachments.value = []
  parseInitialAttachments()
  scrollToTop()
})

const textPropertyElementSelector = computed(
  () =>
    `[data-name="${textPropertyName.value}"][data-object-id="${textPropertyObjectId.value}"][data-behaviour="live-inputs-file-collection-input"]`
)

onMounted(() => {
  window.eventHub.$on('sf-live-inputs:savedChanges', () =>
    parseInitialAttachments()
  )
  window.eventHub.$on('unreview-confirmation-cancelled', cancelActiveUploads)
})

onBeforeUnmount(() => {
  cancelActiveUploads()

  window.eventHub.$off('sf-live-inputs:savedChanges', () =>
    parseInitialAttachments()
  )
  window.eventHub.$off('unreview-confirmation-cancelled', cancelActiveUploads)
})

// Initialize attachments on component mount
parseInitialAttachments()

function parseInitialAttachments(): void {
  const textPropertyElement = document.querySelector(
    textPropertyElementSelector.value
  ) as HTMLElement

  const liquidDocuments = JSON.parse(
    textPropertyElement?.dataset?.value || '[]'
  ) as LiquidDocument[]

  loadAttachments(liquidDocuments)
}

function scrollToTop() {
  const sidebarElement = document.getElementById('right-drawer') as HTMLElement
  sidebarElement.scrollTop = 0
}

function deleteAttachment(attachment: Attachment): void {
  toggleChangesAreDisabled()

  const targetElement = document.querySelector(
    textPropertyElementSelector.value
  ) as HTMLElement

  window.eventHub.$emit('attachments-sidebar:update-attachments', {
    targetElement,
    newAttachments: [],
    attachmentsToDelete: [
      {
        documentId: attachment.document_id,
        documentType: attachment.linkType,
      },
    ],
    errorCallback: (
      xhr: UpdateTextPropertyErrorResponse,
      _status: string,
      _error: string
    ) => {
      const message = xhr.responseJSON?.message
      if (message) {
        raiseError(message)
      }
      toggleChangesAreDisabled()
    },
  })
}

function attachExistingDocuments(
  selectedDocuments: RawExistingDocument[] | RawExistingPermanentDocument[]
) {
  if ('document' in selectedDocuments[0] && selectedDocuments[0].document) {
    attachPeriodSpecificDocuments(selectedDocuments as RawExistingDocument[])
  } else {
    attachPermanentDocuments(
      selectedDocuments as RawExistingPermanentDocument[]
    )
  }
}

function attachPeriodSpecificDocuments(documents: RawExistingDocument[]) {
  const newAttachments: ExistingDocument[] = documents.map((document) => ({
    name: document.title,
    document_id: document.id,
    linkType: DocumentLinkType.Document,
    unique_id: `existing-document-${document.id}`,
  }))

  saveNewAttachments(newAttachments)
}

function attachPermanentDocuments(documents: RawExistingPermanentDocument[]) {
  const newAttachments: ExistingPermanentDocument[] = documents.map(
    (document) => ({
      permanent_document_id: document.id,
      name: document.title,
      document_id: document.documentId,
      linkType: DocumentLinkType.PermanentDocument,
      unique_id: `permanent-document-${document.id}`,
    })
  )

  saveNewAttachments(newAttachments)
}

function attachExistingExports(exports: RawExistingExport[]) {
  const newAttachments: ExistingExport[] = exports.map((entry) => ({
    export_id: entry.id,
    name: entry.title,
    document_id: entry.id,
    linkType: DocumentLinkType.Export,
    unique_id: `export-${entry.id}`,
  }))

  saveNewAttachments(newAttachments)
}

function handleAttachExternalLink(params: {
  name: string
  url: string
  id?: number
}): void {
  const newAttachment = {
    document_id: params.id ? params.id : NaN,
    unique_id: `external-link-${params.name}`,
    name: params.name,
    url: params.url,
    linkType: DocumentLinkType.ExternalLink,
    size: 0,
    previewable: false,
  }

  saveNewAttachments([newAttachment])
}

function handleDrawerClose(): void {
  closeRightDrawerWithoutPersisting(true)
}

const externalManagerTitle = computed(() =>
  currentExternalLink.value
    ? I18n.t('external_links.manager.edit')
    : I18n.t('external_links.manager.attach')
)

// Clear previous auto-linked keys at the start of each new upload batch so that
// the wasAutoLinked watcher in attachment-list-item fires again (false → true).
// We track this here because beforeUpload is the earliest per-file hook and there
// is no batch-start callback from the dropzone. Reset in handleAllFilesUploaded.
let isFirstFileInBatch = true

async function beforeUpload({
  file,
}: {
  file: File
  uuid: string
}): Promise<boolean> {
  if (isFirstFileInBatch) {
    clearAutoLinkedAttachments()
    isFirstFileInBatch = false
  }

  const tempAttachment = {
    document_id: -1,
    name: file.name,
    size: file.size,
    linkType: DocumentLinkType.DirectUpload,
    previewable: false,
  }

  try {
    const results = await checkForDuplicateRequest([tempAttachment])
    const result = results[0]

    if (result.status === 'fulfilled' && result.value?.duplicateOf) {
      const duplicate = result.value.duplicateOf
      const storageKey = generateStorageKey(tempAttachment)
      markAsAutoLinked(storageKey)

      const alreadyAttached = attachments.value.some(
        (att) => !att.uploadId && att.document_id === duplicate.id
      )
      const alreadyPending = pendingAutoLinkedAttachments.value.some(
        (att) => att.document_id === duplicate.id
      )
      if (!alreadyAttached && !alreadyPending) {
        pendingAutoLinkedAttachments.value.push({
          document_id: duplicate.id,
          name: file.name,
          size: file.size,
          linkType: DocumentLinkType.Document,
          previewable: false,
        })
      }

      return false
    }
  } catch (e) {
    SfLog.warn('Error checking for duplicate before upload:', {
      extra: {
        error: e,
        firmId: window.Silverfin.currentFirmId,
        companyId: window.Silverfin.currentCompanyId,
      },
    })
  }

  return true
}

async function handleAllFilesUploaded(status: boolean): Promise<void> {
  isFirstFileInBatch = true

  if (!status) {
    return
  }

  const uploaded = attachments.value.filter((attachment) => attachment.uploadId)
  const newAttachments = [...uploaded, ...pendingAutoLinkedAttachments.value]
  pendingAutoLinkedAttachments.value = []

  if (newAttachments.length === 0) {
    cancelActiveUploads()
  } else {
    saveNewAttachments(newAttachments)
  }
}

function saveNewAttachments(
  newAttachments: NewAttachment[],
  attachmentsToDelete: AttachmentToDelete[] = []
): void {
  if (!isRightDrawerOpen.value) {
    return
  }

  toggleChangesAreDisabled()
  clearError()

  if (newAttachments.length === 0 && attachmentsToDelete.length === 0) {
    // Calling updateAttachments with no data will delete all attachments, so
    // its important to avoid that.
    raiseError(
      'Failed to save attachments. No documents were found to process.'
    )
    onCompleteSaveNewAttachments()
    return
  }

  const targetElement = document.querySelector(
    textPropertyElementSelector.value
  ) as HTMLElement

  window.eventHub.$emit('attachments-sidebar:update-attachments', {
    targetElement,
    newAttachments,
    attachmentsToDelete,
    successCallback: () => {
      // Success callback
      onCompleteSaveNewAttachments()
    },
    errorCallback: (
      xhr: UpdateTextPropertyErrorResponse,
      _status: string,
      _error: string
    ) => {
      // Error callback
      const message = xhr.responseJSON?.message
      if (message) {
        raiseError(message)
      }
      onCompleteSaveNewAttachments()
    },
  })
}

function onCompleteSaveNewAttachments() {
  resetUI()
  closeExistingFilesBrowser()
  toggleChangesAreDisabled()
}

function resetUI() {
  changeMode(attachingModeConstant.NEW_FILES)
  clearExternalLink()
}
</script>
